import type {
  Activity,
  Booking,
  BookingCard,
  CancellationSource,
  HandshakeSessionStatus,
  RegionCode,
} from "@/types";
import { ActivityAvailabilityMode, ActivityPaymentModel } from "@/types";
import type { BookingLifecycleStatus } from "@/types";
import type { BookingLineItemChip } from "./sellerBookingRules";
import { formatCustomerDisplayName } from "./sellerBookingRules";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import i18n from "@/i18n";

export type BookingListType = "service" | "receipt_based";

export interface BookingListRow {
  id: string;
  activityId: string;
  type: BookingListType;
  status: BookingLifecycleStatus;
  cancellationSource?: CancellationSource | null;
  activityName?: string;
  customerDisplayName: string;
  serviceName?: string;
  lineItems: BookingLineItemChip[];
  contactInfo: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  };
  visitAt: string;
  /** Activity payment region (IT/ES) for visit timezone display. */
  activityRegionCode: RegionCode;
  createdAt: string;
  quantity?: number;
  peopleCount?: number;
  timeSlot?: string;
  /** Sum of line totals (service value). Kept for later UI; partner display uses amountPaidCents. */
  totalPriceCents?: number;
  /** Customer-paid checkout total after promo/points. */
  amountPaidCents?: number;
  /** DEPOSIT: seller share still due at the venue. */
  balanceDueAtVenueCents?: number;
  /** True when this booking was priced as DEPOSIT (checkout snapshot). */
  isDeposit?: boolean;
  /** Loyalty points discount from the linked checkout. */
  pointsDiscountCents?: number;
  kioskName?: string;
  receiptUrl?: string;
  receiptTotalCents?: number;
  manualAmountRequired?: boolean;
  latestUploadPath?: string;
  handshakeSessionId?: string | null;
  handshakeMessageCount: number;
  /** Linked handshake session status; null when there is no session. */
  handshakeStatus: HandshakeSessionStatus | null;
  /** Activity setting: whether handshake is enabled for this catalog item. */
  activityRequiresHandshake: boolean;
  /** Activity availability mode for handshake confirm (from catalog when loaded). */
  activityAvailabilityMode?: ActivityAvailabilityMode;
  customerNotes?: string;
  sellerNotes?: string;
  requiresHandshake: boolean;
  isReceiptBased: boolean;
}

type BookingListSource = Booking | BookingCard;

function activityDisplayName(activity: Activity): string {
  return resolveCatalogTranslation(
    activity.name_translations,
    i18n.language,
  );
}

function isFullBooking(booking: BookingListSource): booking is Booking {
  return "device_id" in booking;
}

function buildLineItems(
  booking: BookingListSource,
): BookingLineItemChip[] {
  if (!booking.items.length) {
    return [];
  }
  if (isFullBooking(booking)) {
    return booking.items.map((line) => ({
      id: line.id,
      quantity: line.quantity,
      name: line.activity_item_name ?? "Service",
      unitPriceCents: line.unit_price_cents,
      totalPriceCents: line.total_price_cents,
      listUnitPriceCents: line.list_unit_price_cents,
      listTotalPriceCents: line.list_total_price_cents,
      sellerDiscountCents: line.seller_discount_cents,
      originalUnitPriceCents: line.original_unit_price_cents,
      originalTotalPriceCents: line.original_total_price_cents,
      flitripDiscountCents: line.discount_cents,
    }));
  }
  return booking.items.map((line) => ({
    id: line.id,
    quantity: line.quantity,
    name: line.activity_item_name ?? "Service",
  }));
}

export function mapBookingToListRow(
  booking: BookingListSource,
  options: {
    activityMap?: Map<string, Activity>;
    customerMode: boolean;
    /** When the activity catalog isn't loaded (e.g. admin aggregate detail). */
    activityRequiresHandshake?: boolean;
  },
): BookingListRow {
  const { activityMap, customerMode } = options;
  const activity = activityMap?.get(booking.activity_id);
  const isReceiptBased =
    activity?.payment_model === ActivityPaymentModel.RECEIPT_BASED ||
    booking.activity_payment_model === ActivityPaymentModel.RECEIPT_BASED;

  const activityName =
    booking.activity_name ||
    (activity ? activityDisplayName(activity) : undefined);

  const lineItems = buildLineItems(booking);

  const itemSummary =
    booking.activity_item_name ||
    (lineItems.length > 0
      ? lineItems.map((line) => `${line.quantity}x ${line.name}`).join(", ")
      : undefined);

  const activityRequiresHandshake =
    options.activityRequiresHandshake === true ||
    activity?.requires_handshake === true;
  const requiresHandshake =
    activityRequiresHandshake || !!booking.handshake_session_id;

  const detail = isFullBooking(booking) ? booking : undefined;

  const base = {
    id: booking.id,
    activityId: booking.activity_id,
    type: isReceiptBased ? ("receipt_based" as const) : ("service" as const),
    status: booking.status,
    cancellationSource: booking.cancellation_source ?? null,
    activityName,
    customerDisplayName: formatCustomerDisplayName(
      booking.contact_first_name,
      booking.contact_last_name,
    ),
    lineItems,
    contactInfo: {
      email: detail?.contact_email || undefined,
      phone: detail?.contact_phone || undefined,
      firstName: booking.contact_first_name,
      lastName: booking.contact_last_name,
    },
    visitAt: booking.visit_at,
    activityRegionCode: booking.activity_region_code,
    createdAt: booking.created_at,
    kioskName: detail?.device_name,
    receiptUrl: customerMode
      ? detail?.customer_receipt_url
      : booking.partner_receipt_url ?? undefined,
    receiptTotalCents: customerMode
      ? detail?.customer_receipt_total_cents
      : booking.partner_receipt_total_cents ?? undefined,
    manualAmountRequired: booking.partner_receipt_manual_amount_required,
    latestUploadPath: booking.partner_receipt_latest_upload_path ?? undefined,
    handshakeSessionId: booking.handshake_session_id,
    handshakeMessageCount: booking.handshake_message_count,
    handshakeStatus: booking.handshake_status,
    activityRequiresHandshake,
    activityAvailabilityMode: activity?.availability_mode,
    customerNotes: booking.customer_notes?.trim() || undefined,
    sellerNotes: booking.seller_notes?.trim() || undefined,
    requiresHandshake,
    isReceiptBased,
  };

  const pointsDiscountCents =
    booking.points_discount_cents != null && booking.points_discount_cents > 0
      ? booking.points_discount_cents
      : undefined;

  const isDeposit = booking.activity_payment_model === ActivityPaymentModel.DEPOSIT;
  const balanceDueAtVenueCents =
    isDeposit && booking.balance_due_at_venue_cents != null && booking.balance_due_at_venue_cents > 0
      ? booking.balance_due_at_venue_cents
      : undefined;

  if (isReceiptBased) {
    return {
      ...base,
      peopleCount: booking.people_count ?? undefined,
      timeSlot: booking.time_slot,
      amountPaidCents: booking.amount_paid_cents ?? undefined,
      pointsDiscountCents,
      isDeposit: false,
    };
  }

  return {
    ...base,
    serviceName: itemSummary,
    quantity: booking.quantity,
    timeSlot: booking.time_slot,
    totalPriceCents: booking.total_price_cents ?? undefined,
    amountPaidCents: booking.amount_paid_cents ?? undefined,
    pointsDiscountCents,
    balanceDueAtVenueCents,
    isDeposit,
  };
}
